import type { LocalPoint } from "@excalidraw/math";

import type { ExcalidrawElement, ExcalidrawLineElement } from "../types";

/** bezier control-point offset, relative to its anchor point */
export type PenHandle = Readonly<LocalPoint>;

/** null = corner node; missing `in`/`out` = no handle on that side */
export type PenPointHandles = Readonly<{
  in?: PenHandle;
  out?: PenHandle;
}> | null;

export type PenPathElement = ExcalidrawLineElement &
  Readonly<{ handles: readonly PenPointHandles[] }>;

export const isPenPath = (
  element: ExcalidrawElement | null | undefined,
): element is PenPathElement =>
  element != null &&
  element.type === "line" &&
  (element as ExcalidrawLineElement).handles != null;

const isHandle = (value: unknown): value is PenHandle =>
  Array.isArray(value) &&
  value.length === 2 &&
  typeof value[0] === "number" &&
  typeof value[1] === "number" &&
  Number.isFinite(value[0]) &&
  Number.isFinite(value[1]);

/** validates untrusted `handles` (restore/import); undefined = drop them */
export const sanitizePenHandles = (
  points: readonly LocalPoint[],
  handles: unknown,
): readonly PenPointHandles[] | undefined => {
  if (!Array.isArray(handles) || handles.length !== points.length) {
    return undefined;
  }
  const sanitized: PenPointHandles[] = [];
  for (const entry of handles) {
    if (entry == null) {
      sanitized.push(null);
      continue;
    }
    if (typeof entry !== "object") {
      return undefined;
    }
    const { in: hIn, out: hOut } = entry as { in?: unknown; out?: unknown };
    if (
      (hIn !== undefined && !isHandle(hIn)) ||
      (hOut !== undefined && !isHandle(hOut)) ||
      (hIn === undefined && hOut === undefined)
    ) {
      return undefined;
    }
    sanitized.push({
      ...(hIn !== undefined ? { in: hIn as PenHandle } : {}),
      ...(hOut !== undefined ? { out: hOut as PenHandle } : {}),
    });
  }
  return sanitized;
};
